import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { authSelectors } from '../../store/selectors/auth';
import { profileSelectors } from '../../store/selectors/profile';
import { noticeSeen, noticesPending } from '../../api/notices';
import usePreviewFeature from '../../hooks/usePreviewFeature';

import './calendar-promo.style.scss';

const CALENDAR_PROMO = 'student-calendar-promo';

// Скільки чекаємо, поки студент закриє чужу модалку (ДЗ, телеграм-бот, день
// народження). Промо — найменш терміновий екран у кабінеті, тому воно завжди
// пропускає інших уперед і НЕ позначається показаним, поки не з'явилось на очі.
const QUEUE_STEP_MS = 700;
const QUEUE_MAX_STEPS = 40;

// Власний клас блокування скролу. Спільний `body.open` ставлять і знімають
// Modals / NotificationHeader / FinancesPayBtns — чуже закриття розблокувало б
// скрол під нашою модалкою.
const BODY_CLASS = 'cpromo-open';

// Чужі модалки кабінету бувають двох видів: `.modal-info.open` (Modals.jsx) і
// компоненти без цієї розмітки (ModalEditProfile, NotificationHeader,
// FinancesPayBtns…), які лишають по собі лише спільний `body.open`.
const isForeignModalOpen = () => Boolean(document.querySelector('.modal-info.open'))
  || document.body.classList.contains('open');

export const CalendarIcon = () => (
  <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <rect x="5" y="9" width="38" height="34" rx="6" fill="#fff" stroke="currentColor" strokeWidth="3" />
    <path d="M5 18h38" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    <path d="M15 5v8M33 5v8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    <path d="M17 29l5 5 10-10" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/**
 * Одноразова модалка-анонс «підключи Google Calendar».
 *
 * Кого і коли показувати, вирішує бекенд (`GET /notices/pending`): студент,
 * календар ще не підключений, анонс ще не показували. Тут — лише черга показу,
 * рендер і підтвердження показу.
 */
const CalendarPromoModal = () => {
  const navigate = useNavigate();
  const auth = useSelector(authSelectors);
  const profile = useSelector(profileSelectors);
  const token = auth.token;
  const showImage = usePreviewFeature('courses-redesign');

  // Профіль вирішує, чи буде чужа модалка (ДЗ, телеграм-бот, день народження):
  // Modals.jsx читає той самий обʼєкт. Поки він не приїхав, стан черги
  // невідомий — і промо може відкритись за мить ДО чужої модалки.
  const profileReady = Object.keys(profile.user || {}).length > 0;

  const [notice, setNotice] = useState(null);
  const [open, setOpen] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  // Щоб не смикати /notices/pending повторно на кожному ре-рендері лейауту
  // (він живе весь час, поки відкритий кабінет). Тримаємо саме токен, а не
  // прапорець: після виходу і входу іншою людиною запит має піти заново.
  const requested = useRef(null);
  const confirmed = useRef(false);
  const boxRef = useRef(null);

  // Гасимо саме анонс, а не лише прапорець показу: інакше ефект черги нижче
  // побачив би «notice є, open=false» і миттєво відкрив модалку знову.
  const close = useCallback(() => {
    setOpen(false);
    setNotice(null);
  }, []);

  useEffect(() => {
    if (!token || requested.current === token) {
      return;
    }

    requested.current = token;

    let cancelled = false;

    const load = async () => {
      try {
        const response = await noticesPending({ token });
        const list = response?.data?.data?.notices;

        if (cancelled || !Array.isArray(list)) {
          return;
        }

        const promo = list.find((item) => item?.key === CALENDAR_PROMO);

        if (promo) {
          setNotice(promo);
        }
      } catch (error) {
        // Анонс — необов'язковий екран: якщо запит не пройшов, просто нічого не
        // показуємо. Наступне завантаження кабінету спробує ще раз.
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // Черга: показуємо, щойно приїхав профіль і на екрані немає чужої модалки.
  useEffect(() => {
    if (!notice || open || !profileReady) {
      return undefined;
    }

    if (!isForeignModalOpen()) {
      setOpen(true);

      return undefined;
    }

    let steps = 0;

    const timer = setInterval(() => {
      steps += 1;

      if (!isForeignModalOpen()) {
        clearInterval(timer);
        setOpen(true);

        return;
      }

      if (steps >= QUEUE_MAX_STEPS) {
        clearInterval(timer);
      }
    }, QUEUE_STEP_MS);

    return () => clearInterval(timer);
  }, [notice, open, profileReady]);

  // Показали — підтверджуємо. Саме показ, а не натискання кнопки: людина могла
  // просто закрити вікно, і другий раз це вже не новина.
  useEffect(() => {
    if (!open || !token || !notice || confirmed.current) {
      return;
    }

    confirmed.current = true;

    // Ключ беремо з відповіді, а не з константи: перейменують його на бекенді —
    // підтвердження показу має поїхати за новим ключем, а не тихо впасти в 422.
    noticeSeen({ token, key: notice.key }).catch(() => {
      // Не змогли записати — модалка просто прийде ще раз наступного разу.
    });
  }, [open, token, notice]);

  useEffect(() => {
    document.body.classList.toggle(BODY_CLASS, open);

    return () => document.body.classList.remove(BODY_CLASS);
  }, [open]);

  // Фокус переїжджає в модалку: інакше скрінрідер і клавіатура лишились би на
  // сторінці під оверлеєм.
  useEffect(() => {
    if (open) {
      boxRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  if (!open || !notice) {
    return null;
  }

  const goToSettings = () => {
    close();
    navigate(notice.target || '/settings');
  };

  return (
    <div className="cpromo" role="dialog" aria-modal="true" aria-label={notice.title}>
      <div className="cpromo__overlay" onClick={close} aria-hidden="true" />

      <div className="cpromo__box" ref={boxRef} tabIndex={-1}>
        <button type="button" className="cpromo__close" onClick={close} aria-label="Закрити">
          ✕
        </button>

        {imageFailed || !notice.image || !showImage ? (
          <div className="cpromo__icon">
            <CalendarIcon />
          </div>
        ) : (
          <img
            className="cpromo__image"
            src={notice.image}
            alt=""
            onError={() => setImageFailed(true)}
          />
        )}

        {notice.title && <h3 className="cpromo__title">{notice.title}</h3>}

        {notice.text && (
          <div
            className="cpromo__text"
            dangerouslySetInnerHTML={{ __html: notice.text }}
          />
        )}

        <div className="cpromo__btns">
          {notice.primary_button && (
            <button type="button" className="cpromo__btn cpromo__btn--main" onClick={goToSettings}>
              {notice.primary_button}
            </button>
          )}

          {notice.secondary_button && (
            <button type="button" className="cpromo__btn cpromo__btn--ghost" onClick={close}>
              {notice.secondary_button}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarPromoModal;
